import { Navigate, useMatch } from "react-router-dom";
import { connect } from "react-redux";
import { Box, CircularProgress, Typography } from "@mui/material";
import { IUserResponse } from "../../interfaces";
import { useAppSelector } from "../../hooks";
import AccountLockedModal from "./AccountLockedModal";
import { useSystemTrayCheck } from "../../hooks/useSystemTrayCheck";
import { useTrayAppAvailability } from "../../hooks/useTrayAppAvailability";
import TrayAppDownloadModal from "../atoms/tray-app-download-modal/TrayAppDownloadModal";
import { useLocation } from "react-router-dom";
import { Colors } from "../../configs/colors";

// Shown for the brief window right after login (or on a fresh page load)
// while useAppInitializer rehydrates the station/cashier session from
// localStorage. Without this gate, station-dependent UI (e.g. Lobby's
// action buttons, which key off activeStationCashier) could render once
// with stale/empty state before that rehydration effect has run.
const StationInitializingScreen = () => (
  <Box
    sx={{
      width: "100%",
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "1rem",
      backgroundColor: Colors.BACKGROUND_GRAY,
    }}
  >
    <CircularProgress sx={{ color: Colors.PRIMARY }} />
    <Typography
      sx={{
        fontSize: "var(--fs-base)",
        fontWeight: 600,
        color: Colors.TEXT_GRAY_DARK,
      }}
    >
      Initializing your station...
    </Typography>
  </Box>
);

const PrivateRoute = (props: {
  token?: string;
  children: JSX.Element;
  user?: IUserResponse;
  authDetailsInitialized?: boolean;
  disableAdminGuard?: boolean;
  disablePosGuard?: boolean;
}) => {
  const location = useLocation();
  const isOnboardingPage = useMatch("/pos/onboarding");

  // Enable check only if NOT on onboarding page AND NOT on admin pages
  const shouldCheckSystemTray = !isOnboardingPage && !location.pathname.startsWith("/admin");
  useSystemTrayCheck(shouldCheckSystemTray);

  const { checking: trayChecking, available: trayAvailable, downloadUrl: trayDownloadUrl, version: trayVersion } =
    useTrayAppAvailability(shouldCheckSystemTray);

  // Station data itself is fetched app-wide by useAppInitializer (device-level,
  // not tied to the logged-in user) — this component only reads the result.
  const { activeStation, stationCashierHydrated } = useAppSelector(
    (state) => state.cashierStation
  );

  const { accountLocked } = useAppSelector((state) => state.auth);

  // Block locked accounts — cannot bypass
  if (accountLocked) {
    return <AccountLockedModal />;
  }

  if (props.user) {
    const roleName = props.user.role.name;
    const permissions = props.user.role.permissions?.map((p) => p.name) ?? [];

    const isAdminUser =
      roleName === "ADMIN" ||
      roleName === "MERCHANT_ADMIN" ||
      permissions.includes("MANAGER");
    const isCashierUser =
      !isAdminUser && permissions.includes("CASHIER");

    if (isAdminUser && !props.disableAdminGuard) {
      return <Navigate to={"/admin/dashboard"} />;
    } else if (isCashierUser && !props.disablePosGuard) {
      return <Navigate to={"/pos/lobby"} />;
    }
  }
  if (!props.authDetailsInitialized) {
    return <p>Loading...</p>;
  }

  // Cashier/POS routes read activeStationCashier (e.g. Lobby's action
  // buttons, Void/Refund). Hold off rendering them until useAppInitializer
  // has actually attempted to rehydrate it from localStorage, instead of
  // rendering once against stale/empty state right after login.
  if (
    props.token !== null &&
    props.user !== null &&
    props.disableAdminGuard === undefined &&
    !stationCashierHydrated
  ) {
    return <StationInitializingScreen />;
  }

  const children =
    (!activeStation ||
      activeStation.accessGranted === "DENIED" ||
      activeStation.accessGranted === "PENDING") &&
      props.disableAdminGuard === undefined &&
      !isOnboardingPage ? (
      <Navigate to="/pos/onboarding" />
    ) : (
      props.children
    );

  if (props.token !== null && props.user !== null) {
    return (
      <>
        {shouldCheckSystemTray && !trayChecking && !trayAvailable && (
          <TrayAppDownloadModal
            open={true}
            downloadUrl={trayDownloadUrl}
            version={trayVersion}
            loading={trayChecking}
            blocking={true}
          />
        )}
        {children}
      </>
    );
  }

  return <Navigate to={"/auth/login"} />;
};

const mapStateToProps = (state: any) => state.auth;
export default connect(mapStateToProps, null)(PrivateRoute);
